/**
 * Timer Service
 * タイマー管理に特化したサービス
 *
 * 責任:
 * - インターバルタイマーの管理
 * - ワンショットタイマーの管理
 * - タイマーのクリーンアップ
 */

// ==========================================
// Type Definitions
// ==========================================

/**
 * タイマーコールバック関数の型
 */
export type TimerCallback = () => void;
import { logger } from '@/utils/logger.js';

/**
 * タイマー設定
 */
export interface TimerConfig {
    /** タイマー間隔（ミリ秒） */
    interval: number;
    /** 自動開始フラグ */
    autoStart?: boolean;
    /** ティック時のコールバック */
    onTick?: TimerCallback;
}

/**
 * タイマー状態
 */
export interface TimerState {
    /** 実行中フラグ */
    running: boolean;
    /** 一時停止フラグ */
    paused: boolean;
    /** 経過時間（ミリ秒） */
    elapsed: number;
    /** 残り時間（ミリ秒） */
    remaining: number;
}

/**
 * タイマー統計情報
 */
export interface TimerStats {
    /** ティック回数 */
    ticks: number;
    /** 総経過時間（ミリ秒） */
    totalElapsed: number;
    /** 平均ティック間隔（ミリ秒） */
    avgTickDuration: number;
}

/**
 * サービスオプション
 */
export interface ServiceOptions {
    /** ロガー関数 */
    logger?: (message: string, ...args: unknown[]) => void;
    /** 統計情報を有効化 */
    enableStats?: boolean;
}

/**
 * インターバルタイマー情報
 */
export interface IntervalTimerInfo {
    /** タイマーID */
    id: NodeJS.Timeout;
    /** インターバル（ミリ秒） */
    interval: number;
    /** 開始時刻（UNIXタイムスタンプ） */
    startTime: number;
    /** コールバック関数 */
    callback: TimerCallback;
}

/**
 * タイムアウトタイマー情報
 */
export interface TimeoutTimerInfo {
    /** タイマーID */
    id: NodeJS.Timeout;
    /** 遅延時間（ミリ秒） */
    delay: number;
    /** スケジュール時刻（UNIXタイムスタンプ） */
    scheduledTime: number;
    /** コールバック関数 */
    callback: TimerCallback;
}

/**
 * インターバルタイマーステータス
 */
export interface IntervalStatus {
    /** タイマー名 */
    name: string;
    /** インターバル（ミリ秒） */
    interval: number;
    /** 実行時間（ミリ秒） */
    runningFor: number;
}

/**
 * スケジュールタイマーステータス
 */
export interface ScheduledStatus {
    /** タイマー名 */
    name: string;
    /** 遅延時間（ミリ秒） */
    delay: number;
    /** 残り時間（ミリ秒） */
    remainingTime: number;
}

/**
 * タイマーサービス全体のステータス
 */
export interface TimerServiceStatus {
    /** アクティブなインターバル数 */
    activeIntervals: number;
    /** スケジュール済みタイムアウト数 */
    scheduledTimeouts: number;
    /** インターバル一覧 */
    intervals: IntervalStatus[];
    /** スケジュール済みタイマー一覧 */
    scheduled: ScheduledStatus[];
}

// ==========================================
// TimerService Class
// ==========================================

/**
 * タイマーサービスクラス
 * インターバルとタイムアウトを一元管理
 */
class TimerService {
    /** インターバルタイマーのマップ */
    private intervals: Map<string, IntervalTimerInfo>;

    /** タイムアウトタイマーのマップ */
    private timeouts: Map<string, TimeoutTimerInfo>;

    /** 次のタイマーID */
    private nextId: number;

    /**
     * コンストラクタ
     */
    constructor() {
        this.intervals = new Map<string, IntervalTimerInfo>();
        this.timeouts = new Map<string, TimeoutTimerInfo>();
        this.nextId = 1;
    }

    /**
     * インターバルタイマーを開始
     *
     * @param intervalMs - インターバル（ミリ秒）
     * @param callback - ティック時のコールバック
     * @param name - タイマー名（オプション）
     * @returns タイマー名
     */
    startInterval(
        intervalMs: number,
        callback: TimerCallback,
        name: string | null = null
    ): string {
        const id = this.nextId++;
        const timerName = name || `interval-${id}`;

        // 既存のタイマーがあれば停止
        if (this.intervals.has(timerName)) {
            this.stopInterval(timerName);
        }

        const intervalId = setInterval(() => {
            try {
                callback();
            } catch (error) {
                logger.error(`Error in interval timer ${timerName}:`, error);
            }
        }, intervalMs);

        this.intervals.set(timerName, {
            id: intervalId,
            interval: intervalMs,
            startTime: Date.now(),
            callback
        });

        return timerName;
    }

    /**
     * インターバルタイマーを停止
     *
     * @param name - タイマー名
     * @returns 停止成功/失敗
     */
    stopInterval(name: string): boolean {
        const timer = this.intervals.get(name);
        if (timer) {
            clearInterval(timer.id);
            this.intervals.delete(name);
            return true;
        }
        return false;
    }

    /**
     * ワンショットタイマーをスケジュール
     *
     * @param delayMs - 遅延時間（ミリ秒）
     * @param callback - 実行時のコールバック
     * @param name - タイマー名（オプション）
     * @returns タイマー名
     */
    scheduleOnce(
        delayMs: number,
        callback: TimerCallback,
        name: string | null = null
    ): string {
        const id = this.nextId++;
        const timerName = name || `timeout-${id}`;

        // 既存のタイマーがあればキャンセル
        if (this.timeouts.has(timerName)) {
            this.cancelScheduled(timerName);
        }

        const timeoutId = setTimeout(() => {
            this.timeouts.delete(timerName);
            try {
                callback();
            } catch (error) {
                logger.error(`Error in timeout timer ${timerName}:`, error);
            }
        }, delayMs);

        this.timeouts.set(timerName, {
            id: timeoutId,
            delay: delayMs,
            scheduledTime: Date.now(),
            callback
        });

        return timerName;
    }

    /**
     * スケジュール済みタイマーをキャンセル
     *
     * @param name - タイマー名
     * @returns キャンセル成功/失敗
     */
    cancelScheduled(name: string): boolean {
        const timer = this.timeouts.get(name);
        if (timer) {
            clearTimeout(timer.id);
            this.timeouts.delete(name);
            return true;
        }
        return false;
    }

    /**
     * すべてのタイマーを停止
     */
    stopAll(): void {
        // インターバルを停止
        this.intervals.forEach((timer) => {
            clearInterval(timer.id);
        });
        this.intervals.clear();

        // タイムアウトをキャンセル
        this.timeouts.forEach((timer) => {
            clearTimeout(timer.id);
        });
        this.timeouts.clear();
    }

    /**
     * タイマーの状態を取得
     *
     * @returns タイマーサービスのステータス
     */
    getStatus(): TimerServiceStatus {
        const now = Date.now();

        const intervals = Array.from(this.intervals.entries()).map(([name, timer]): IntervalStatus => ({
            name,
            interval: timer.interval,
            runningFor: now - timer.startTime
        }));

        const scheduled = Array.from(this.timeouts.entries()).map(([name, timer]): ScheduledStatus => ({
            name,
            delay: timer.delay,
            remainingTime: Math.max(0, timer.delay - (now - timer.scheduledTime))
        }));

        return {
            activeIntervals: intervals.length,
            scheduledTimeouts: scheduled.length,
            intervals,
            scheduled
        };
    }

    /**
     * 特定のタイマーが存在するかチェック
     *
     * @param name - タイマー名
     * @returns タイマーの存在確認
     */
    hasTimer(name: string): boolean {
        return this.intervals.has(name) || this.timeouts.has(name);
    }

    /**
     * クリーンアップ
     * すべてのタイマーを停止し、リソースを解放
     */
    cleanup(): void {
        this.stopAll();
    }
}

// ==========================================
// Module Exports
// ==========================================

// Export for CommonJS
// ES6 Export
export { TimerService };

if (typeof module !== 'undefined' && module.exports) {
    module.exports = TimerService;
}

// Export for Browser
declare global {
    interface Window {
        TimerService: typeof TimerService;
    }
}

// Export default for ES Modules
export default TimerService;
